import { defineStore } from 'pinia';
import useApi from '@/composeables/useApi';
import { AppCustomization, LogInResponse, User } from '@/constants/types/api';
import {
  Checklist,
  GroupEmergencyPost,
  GroupMembership,
  GroupPostEmergencyUserReply,
  NotificationSetting
} from '@/constants/types';
import useLocalStorage from '@/composeables/useLocalStorage';
import { useIncidentStore } from '@/store/incident';
import shadeColor from '@/utils/shade-color';
import { simpleAlert } from '@/utils/alert';
import isColorLight from '@/utils/is-color-light';
import { useWarningsStore, WarningRequestType } from '@/store/warnings';
import { useUiStore } from '@/store/uiStore';
import {useAzureAuth} from "@/composeables/useAzureAuth";

export const useUserStore = defineStore('userStore', {
  state: () => ({
    accountToken: null as string | null,
    currentUser: null as User | null,
    hasFetchedUser: false,
    userGroupMemberships: [] as GroupMembership[],
    userNotificationSettings: [] as NotificationSetting[],
    userGroupsLoaded: false,
    latestEmergency: null as GroupPostEmergencyUserReply & { group_post: GroupEmergencyPost } | null,
    myChecklists: [] as Checklist[],
    themeName: 'Svensk Handel',
    darkText: false,
    backgroundDarkText: false,
  }),
  actions: {
    async fetchUser() {
      const storage = await useLocalStorage();
      const { apiGet } = useApi();
      const uiStore = useUiStore();
      this.hasFetchedUser = false;
      const accountToken = this.accountToken || await storage.get('accountToken');
      if (accountToken) {
        const res = await apiGet<User>('/users/me');
        if (res) {
          this.currentUser = res;

          uiStore.onShowCompanyInput(!res.completed_profile);

          if (this.themeName !== res.app_customization.name) {
            this.setTheme(res.app_customization);
          }
        }
      }
      this.hasFetchedUser = true;
    },

    async fetchUserGroups(fromCache = true) {
      if (!this.userGroupsLoaded || !fromCache) {
        if (this.currentUser?.id) {
          const { apiGet, handleApiError } = useApi();
          const url = `/users/${this.currentUser.id}/groups?per_page=25`;
          const res = await apiGet<GroupMembership[]>(url, null, fromCache);
          if (res) {
            this.userGroupMemberships = res;
            this.userGroupsLoaded = true;

          } else {
            await handleApiError();
          }
          return true;
        } else {
          console.error('No user id');
        }
      }
      return false;
    },
    async fetchUserNotificationSettings() {
      if (this.currentUser?.id) {
        const { apiGet, handleApiError } = useApi();
        const res = await apiGet<NotificationSetting[]>(`/users/${this.currentUser.id}/notificationsettings`, null, false);
        if (res) {
          this.userNotificationSettings = res;

        } else {
          await handleApiError();
        }
        return true;
      }
    },

    /**
     * @todo - this gets my replies to ongoing situations
     */
    async fetchEmergencies() {
      const { apiGet } = useApi();
      const res = await apiGet<GroupPostEmergencyUserReply[]>('/users/me/emergencies');
      if (res && res.length) {
        this.latestEmergency = res[0];
      }
    },
    async fetchMyChecklists() {
      const { apiGet, handleApiError } = useApi();
      const res = await apiGet<Checklist[]>(`/users/${this.currentUser.id}/checklists`, null, false);
      if (res) {
        this.myChecklists = res;
      } else {
        await handleApiError();
      }
    },
    async signIn(signInData: LogInResponse) {
      const storage = await useLocalStorage();
      this.accountToken = signInData.access_token;
      await storage.set('accountToken', signInData.access_token);
      await storage.set('loggedOutUsage', false);
      await this.fetchUser();
      await this.fetchUserGroups(false);
    },
    async signOut() {
      const { signOut } = useAzureAuth();
      const storage = await useLocalStorage();
      const uiStore = useUiStore();
      this.accountToken = null;
      this.currentUser = null;
      this.latestEmergency = null;
      this.userGroupMemberships = [];
      this.myChecklists = [];
      uiStore.resetSegments();

      this.setTheme();

      await storage.remove('accountToken');
      signOut();
    },
    async deleteAccount() {
      const storage = await useLocalStorage();
      const { apiDelete } = useApi();
      const res = await apiDelete(`/users/${this.currentUser.id}`);

      if (res) {
        this.accountToken = null;
        this.currentUser = null;
        await storage.remove('accountToken');
        return true;
      } else {
        return false;
      }
    },
    async updateUser(payload: {
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
      access_code?: string;
      company?: {
        org_nr: string,
        name: string,
        sector: number,
        access_code: string
      },
      workplace?: {
        name: string,
        id: string | number,
        address: string,
        zip: string,
        city: string
      },
      ice_name?: string,
      ice_mail?: string,
      ice_number?: string,
      membership_number?: string,
    }) {
      const { apiPut } = useApi();
      const uiStore = useUiStore();

      const body = {
        ...this.currentUser,
        ...payload,
      };
      const res = await apiPut<User>(`/users/${this.currentUser.id}`, body);

      if (res) {
        this.currentUser = res;

        uiStore.onShowCompanyInput(!res.completed_profile);

        if (this.themeName !== res.app_customization.name) {
          this.setTheme(res.app_customization);
          const { fetchTemplates } = useIncidentStore();
          const { fetchWarnings } = useWarningsStore();

          await fetchTemplates(false);
          await this.fetchUserGroups(false);
          await this.fetchMyChecklists();
          await fetchWarnings(WarningRequestType.Security);
        }

        await simpleAlert('Användaren sparad!');
        return res;
      } else {
        await simpleAlert('Kunde inte spara användaren, försök igen senare eller kontakta support.');
        return null;
      }
    },
    setTheme(appCustomization?: AppCustomization) {
      const customBackgroundColor = appCustomization?.background_color || '#004F4F';
      let darkCustomBackgroundColor = appCustomization?.dark_background_color || '#337272';
      const darkShaded = appCustomization?.dark_background_color ? shadeColor(darkCustomBackgroundColor, -10) : '#669595';
      if (appCustomization?.background_color && !appCustomization?.dark_background_color) {
        darkCustomBackgroundColor = shadeColor(customBackgroundColor, -15);
      }
      const root = document.querySelector('body');
      root?.style.setProperty('--ion-color-primary', customBackgroundColor);
      root?.style.setProperty('--ion-color-primary-shade', darkCustomBackgroundColor);
      root?.style.setProperty('--ion-color-secondary', darkCustomBackgroundColor);
      root?.style.setProperty('--ion-color-secondary-shade', darkShaded);

      const primaryColorIsLight = isColorLight(customBackgroundColor);
      const primaryBackgroundIsLight = isColorLight(darkCustomBackgroundColor);

      root?.style.setProperty('--ion-custom-text-color', primaryColorIsLight ? '#000' : 'unset');

      if (primaryColorIsLight) {
        root?.style.setProperty('--ion-tab-bar-color-selected', '#000');
        root?.style.setProperty('--ion-secondary-custom-text-color', '#000');
        root?.style.setProperty('--ion-color-contrast', '#000');
        this.darkText = true;
      } else {
        root?.style.setProperty('--ion-tab-bar-color-selected', 'unset');
        root?.style.setProperty('--ion-secondary-custom-text-color', 'var(--ion-color-medium)');
        root?.style.setProperty('--ion-color-contrast', '#FFF');
        this.darkText = false;
      }

      if (primaryBackgroundIsLight) {
        root?.style.setProperty('--ion-background-custom-text-color', '#000');
        this.backgroundDarkText = true;
      } else {
        root?.style.setProperty('--ion-background-custom-text-color', 'unset');
        this.backgroundDarkText = false;
      }
      this.themeName = appCustomization?.name || 'Svensk Handel';
    },
    setGroupMembershipList(list: GroupMembership[]) {
      this.userGroupMemberships = list;
    },
  },
  getters: {
    isStandardTheme(state): boolean {
      return state.themeName === 'Svensk Handel';
    },
    fetchedUserCompleted(): boolean {
      return this.hasFetchedUser;
    },
    checklistById(state) {
      return (id) => state.myChecklists.find(checklist => checklist.id === id);
    },
    activeUserMemberships(state) {
      return state.userGroupMemberships.filter(membership => membership.type === 2 || membership.type === 3);
    },
    userGroups(state) {
      return state.userGroupMemberships
        .map((i) => i.group)
        .sort((a, b) => {
          const dateA = new Date(b.last_activity?.updated_at ?? b.created_at);
          const dateB = new Date(a.last_activity?.updated_at ?? a.created_at);
          return dateA.getTime() - dateB.getTime();
        });
    },
  },
});
